<template>
  <EquipmentBase
    :section-id="ScenarioDetailSectionKey.CABLE"
    :class="{ 'compact-mode': !!stack }"
  >
    <!-- Compact Mode Layout -->
    <template v-if="stack">
      <!-- Header Row: Cables Title & Cable Temperature Toggle -->
      <div class="compact-header-row" style="margin-top: 0.75rem">
        <div class="flex align-items-center">
          <h3 class="compact-title">Cables</h3>
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.CABLE]"
          />
        </div>
        <div class="flex align-items-center">
          <LabelledToggle
            v-model="showCableTemperature"
            text="Cable Temperature"
          />
        </div>
      </div>

      <!-- Cable Temperature Chart - Right below header -->
      <transition name="vertical-slide">
        <CableTemperatureChart
          v-if="showCableTemperature"
          enable-mouse-tracking
          :draw-legend="true"
          :is-horizontal="true"
        />
      </transition>

      <Divider />

      <!-- Stats Row: Pump Depth, Surface Voltage, Surface kVA -->
      <div class="compact-stats-row flex flex-wrap gap-4">
        <ConverterInput
          label="Pump Depth"
          :model-value="pumpDepth"
          :base-unit="LengthUnits.Feet"
          :model="LargeLengthUnits.options()"
          mode="convert"
          class="compact-stat-input"
        />
        <ConverterInput
          label="Surface Voltage"
          :model-value="surfaceVoltage"
          :base-unit="VoltageUnits.Volts"
          :model="VoltageUnits.options()"
          mode="convert"
          class="compact-stat-input"
        />
        <ConverterInput
          label="Surface kVA"
          :model-value="surfaceKva"
          :base-unit="ApparentPowerUnits.KiloVoltAmps"
          :model="ApparentPowerUnits.options()"
          mode="convert"
          class="compact-stat-input"
        />
        <ConverterInput
          v-tooltip.top="
            `Total Recommended Cable Length targets ${
              recommendedTotalCableLengthFt + ' ' + lengthUnit.label
            } above Pump Discharge`
          "
          label="Total Recommended Cable Length"
          :model-value="recommendedTotalCableLengthFt || 0"
          :base-unit="LengthUnits.Feet"
          :model="LargeLengthUnits.options()"
          mode="convert"
          data-cy="total-recommended-cable-length-converter-input"
        />
        <ConverterInput
          v-tooltip.top="
            `Minimum Cable Length targets ${
              depthToReachMLEPlugFt + ' ' + lengthUnit.label
            } above Pump Discharge`
          "
          label="Total Minimum Cable Length"
          :model-value="depthToReachMLEPlugFt || 0"
          :base-unit="LengthUnits.Feet"
          :model="LargeLengthUnits.options()"
          mode="convert"
          data-cy="total-minimum-cable-length-converter-input"
        />
      </div>

      <!-- Shared Cable Rows -->
      <div v-if="cablesOrder?.length > 0" class="space-y-3">
        <CableRow
          v-for="(cablesXrefId, i) in cablesOrder"
          :key="i"
          :index="i"
          :cable="getCable(cablesXrefId)"
          :stack="stack"
          :hide-add-above="newCableExists"
          :hide-add-below="newCableExists"
          :hide-remove="false"
          @add-above="addCable(i)"
          @add-below="addCable(i + 1)"
          @remove="removeCable($event, i)"
          @update:cables="updateCable($event, i)"
        />
      </div>
      <div v-else class="mb-3">
        <CableRow
          :cable="generateCableXref()"
          :stack="stack"
          :hide-add-above="true"
          :hide-add-below="true"
          :hide-remove="false"
          @update:cables="addCable(0, $event)"
        />
      </div>
    </template>

    <!-- Full Mode Layout (default) -->
    <template v-else>
      <EquipmentHeader class="cables-header-full">
        <template #title>
          <div class="flex justify-content-between align-items-center">
            Cables
            <CalculatorToggle
              :section-ids="[EquipmentCalculatorSectionKey.CABLE]"
            />
          </div>
        </template>
        <template #titleRight>
          <LabelledToggle
            v-model="showCableTemperature"
            text="Cable Temperature"
          />
        </template>
      </EquipmentHeader>

      <transition name="vertical-slide">
        <CableTemperatureChart
          v-if="showCableTemperature"
          enable-mouse-tracking
          :draw-legend="true"
          :is-horizontal="true"
        />
      </transition>

      <div
        class="flex flex-wrap justify-content-between align-items-center mb-4"
      >
        <div class="grid">
          <ConverterInput
            class="col"
            label="Pump Depth"
            :model-value="pumpDepth"
            :base-unit="LengthUnits.Feet"
            :model="LargeLengthUnits.options()"
            mode="convert"
          />
          <ConverterInput
            class="col"
            label="Surface Voltage"
            :model-value="surfaceVoltage"
            :base-unit="VoltageUnits.Volts"
            :model="VoltageUnits.options()"
            mode="convert"
          />
          <ConverterInput
            class="col"
            label="Surface kVA"
            :model-value="surfaceKva"
            :base-unit="ApparentPowerUnits.KiloVoltAmps"
            :model="ApparentPowerUnits.options()"
            mode="convert"
          />
          <ConverterInput
            class="col"
            label="Total Recommended Cable Length"
            :model-value="recommendedTotalCableLengthFt || 0"
            :base-unit="LengthUnits.Feet"
            :model="LargeLengthUnits.options()"
            mode="convert"
          />
          <ConverterInput
            class="col"
            label="Total Minimum Cable Length"
            :model-value="depthToReachMLEPlugFt || 0"
            :base-unit="LengthUnits.Feet"
            :model="LargeLengthUnits.options()"
            mode="convert"
          />
        </div>
      </div>

      <!-- Shared Cable Rows -->
      <div v-if="cablesOrder?.length > 0" class="space-y-3">
        <CableRow
          v-for="(cablesXrefId, i) in cablesOrder"
          :key="i"
          :index="i"
          :cable="getCable(cablesXrefId)"
          :stack="stack"
          :hide-add-above="newCableExists"
          :hide-add-below="newCableExists"
          :hide-remove="false"
          @add-above="addCable(i)"
          @add-below="addCable(i + 1)"
          @remove="removeCable($event, i)"
          @update:cables="updateCable($event, i)"
        />
      </div>
      <div v-else class="mb-3">
        <CableRow
          :cable="generateCableXref()"
          :stack="stack"
          :hide-add-above="true"
          :hide-add-below="true"
          :hide-remove="false"
          @update:cables="addCable(0, $event)"
        />
      </div>
    </template>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { VoltageUnits } from "@/libs/units/voltage";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import CableRow from "@/components/equipment/cables/CableRow.vue";
import { v4 } from "uuid";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";
import { injectStrict } from "@/utils/utils";
import {
  cablesFormKey,
  equipmentFormKey,
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import CableTemperatureChart from "@/components/scenarios/charts/CableTemperatureChart.vue";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import type { XrefCables } from "@/composables/forms/formTypes";
import Divider from "primevue/divider";
import type { StackLocation } from "@/components/equipment/d";
import type { ConvertableUnit } from "@/libs/units";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { DEFAULT_CABLE_LENGTH_FEET } from "@/libs/calculations/constants";

const props = defineProps<{
  stack?: StackLocation;
}>();

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const cablesForm = injectStrict(cablesFormKey);
const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const showCableTemperature = ref(false);

const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);
const lengthUnit = computed<ConvertableUnit>(
  () => getLargeLengthUnit("Total Recommended Cable Length.convert.ft").value
);

const depthToReachMLEPlugFt = computed(
  () =>
    scenarioCalculations.equipmentLengths.depthToReachMLEPlugFt.value -
    (equipment.value?.r_mle?.length_feet || 0)
);
const recommendedTotalCableLengthFt = computed(
  () =>
    scenarioCalculations.equipmentLengths.recommendedTotalCableLengthFt.value
);
const pumpDepth = computed(
  () => scenarioCalculations.equipmentLengths.completePumpDepthFt.value
);
const surfaceVoltage = computed(
  () => scenarioCalculations.surfaceDriveCalculations.requiredVoltageV.value
);
const surfaceKva = computed(
  () => scenarioCalculations.surfaceDriveCalculations.requiredKVA.value
);

const cablesOrder = computed(() => {
  const orderKey =
    props.stack === "lower" ? "lower_cables_order" : "cables_order";
  const order = (equipment.value[orderKey] as string[] | undefined) || [];
  return order.filter((cableId: string) => cablesForm.data.value[cableId]);
});

const newCableExists = computed(() => {
  // Check if there are no cables at all
  if (cablesOrder.value?.length === 0) {
    return true;
  }

  // Check if any cables have null cable_id (indicating incomplete configuration)
  return cablesOrder.value.some((cableId: string) => {
    const cable = cablesForm.data.value[cableId];
    return cable && cable.cable_id === null;
  });
});

function generateCableXref(): XrefCables[number] {
  return {
    __typename: "scenario_equipment_xref_cables",
    id: v4(),
    scenario_equipment_id: equipment.value?.id,
    cable_id: null,
    length_feet: 0,
    winning_revision_id: null,
    winning_revision_parent_id: null,
    winning_revision_created_at: "now()",
    winning_revision_updated_at: "now()",
    winning_revision_deleted_at: null,
    r_cable: null
  } as XrefCables[number];
}

function addCable(
  index: number,
  xrefCable?: XrefCables[number]
): XrefCables[number] {
  let lengthFeet = 0;
  // Default length when creating first cable xref
  if (cablesOrder.value.length === 0) {
    lengthFeet = recommendedTotalCableLengthFt.value;
  }

  const cable = xrefCable || generateCableXref();
  cable.length_feet = cable.length_feet || lengthFeet;
  const cableIndex = index < 0 ? 0 : index;
  const orderKey =
    props.stack === "lower" ? "lower_cables_order" : "cables_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(cableIndex, 0, cable.id);
  cablesForm.update({
    [cable.id]: cable
  });
  equipmentForm.update({
    [orderKey]: newOrder
  } as Partial<typeof equipmentForm.data.value>);
  return cable;
}

function removeCable(cable: XrefCables[number], index: number): void {
  const orderKey =
    props.stack === "lower" ? "lower_cables_order" : "cables_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(index, 1);

  // xref exists in db, soft delete
  if (cablesForm.initialData.value?.[cable.id]) {
    cablesForm.update({
      [cable.id]: {
        ...cable,
        journal_deleted_at: "now()"
      }
    });
  }
  // xref was created but not saved, wipe out
  else {
    delete cablesForm.data.value[cable.id];
  }

  equipmentForm.update({
    [orderKey]: newOrder
  } as Partial<typeof equipmentForm.data.value>);
}

function updateCable(cable: XrefCables[number], index: number): void {
  const orderKey =
    props.stack === "lower" ? "lower_cables_order" : "cables_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(index, 1, cable.id);

  let lengthFeet = 0;
  // Default length when updating first cable xref and length is zero
  if (cablesOrder.value.length === 0 && cable.length_feet === 0) {
    lengthFeet =
      scenarioCalculations.equipmentLengths.tubingLengthFt.value +
      DEFAULT_CABLE_LENGTH_FEET;
  }
  cable.length_feet = cable.length_feet || lengthFeet;

  cablesForm.update({
    [cable.id]: cable
  });
  equipmentForm.update({
    [orderKey]: newOrder
  } as Partial<typeof equipmentForm.data.value>);
}

function getCable(id: string) {
  return cablesForm.data.value[id];
}
</script>

<style lang="scss" scoped>
.cables-header-full {
  :deep(.equipment-header) {
    padding: 0.5rem 0 !important;
    gap: 1rem !important;
    margin-bottom: 0.5rem;
    grid-template-columns: minmax(20px, max-content) 1fr !important;

    .left {
      flex-shrink: 0;
    }

    .right {
      display: flex !important;
      flex: 1 1 auto;
      min-width: 0;
      justify-content: flex-start !important;
      align-items: center;
      gap: 1.5rem !important;
      flex-wrap: wrap;

      > * {
        flex: 0 0 auto;
      }

      .flex {
        gap: 1rem;
      }
    }
  }
}

// Reduce padding for cables section
:deep(.equipment-row) {
  padding-top: 1em !important;
}
</style>
